import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Globe } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import Crown from "@/components/Crown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_MARQUEE = [
  "APPLY NOW", "OPEN APPLICATIONS", "GET IN TOUCH", "START YOUR JOURNEY",
];

const ContactMarquee = () => {
  const items = [...CONTACT_MARQUEE, ...CONTACT_MARQUEE, ...CONTACT_MARQUEE, ...CONTACT_MARQUEE];
  return (
    <div className="marquee" data-testid="contact-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const INTERESTS = [
  "1:1 Personal Development Coaching",
  "1:1 Leadership Coaching",
  "1:1 Career Coaching",
  "1:1 Business Coaching",
  "1:1 Brand & Marketing Coaching",
  "Whisper Group Program",
  "GRIT™ Group Program",
  "Sparkle Group Program",
  "Ascend Group Program",
  "Speaking / Corporate Inquiry",
  "Just exploring — not sure yet",
];

const FAQ = [
  {
    q: "What’s the difference between group programs and 1:1 coaching?",
    a: "Group programs (Whisper, GRIT™, Sparkle, Ascend) move through shared curriculum with a cohort of women. 1:1 coaching is fully private and tailored to the season you’re in. Many clients begin in group and add 1:1 later — or vice versa.",
  },
  {
    q: "How do I know which program is right for me?",
    a: "That’s exactly what the complimentary 15-min visionary call is for. Tell me where you are; I’ll tell you the room I’d put you in. No pitch, no pressure.",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes — every program and 1:1 package has a pay-in-full and a payment plan option. We’ll cover both during your call.",
  },
  {
    q: "Are sessions done virtually or in person?",
    a: "Sessions are virtual by default (Zoom). In-person intensives and private retreats are available by request for Ascend and Immersive 1:1 clients.",
  },
  {
    q: "What if I’m not sure I’m ready?",
    a: "Most women I work with weren’t. The work begins with telling that truth out loud. Reach out anyway.",
  },
];

const Contact = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    interest: INTERESTS[0],
    message: "",
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setDone(true);
      toast.success("Your message is in. Nijah’s team will reply within 48 business hours.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HERO */}
      <section className="hero" style={{ minHeight: "auto" }}>
        <div className="container-x" style={{
          padding: "120px 80px 90px",
          color: "#fff",
          maxWidth: 900,
          marginLeft: 0,
        }}>
          <Crown size={42} opacity={0.95} />
          <SectionLabel gold>CONTACT</SectionLabel>
          <h1 className="h-display" style={{
            fontSize: "clamp(40px, 5.4vw, 76px)",
            color: "#fff",
            marginTop: 14,
            lineHeight: 1.04,
          }}>
            We’d love to <span className="italic-c text-gold">hear from you.</span>
          </h1>
          <p className="hero-tagline" style={{ marginTop: 22 }}>
            Have a question about a program? Ready to book your call? Or just
            not sure where to start? Reach out — we respond within 48 hours.
          </p>
          <div className="hero-cta-row" style={{ marginTop: 26 }}>
            <a href="#contact-form" className="btn btn-primary" data-testid="contact-jump-form">
              Send a Message →
            </a>
            <a href="#faq" className="btn btn-ghost" data-testid="contact-jump-faq">
              See FAQs
            </a>
          </div>
        </div>
      </section>

      <ContactMarquee />

      {/* REACH OUT — 3 contact cards */}
      <section className="section-pad bg-cream" data-testid="reach-out">
        <div className="container-x">
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 40px" }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Crown size={32} opacity={0.75} />
            </div>
            <SectionLabel center>REACH OUT</SectionLabel>
            <h2 className="section-title text-teal" style={{ marginTop: 12 }}>
              We’d love to <span className="italic-c text-coral">hear from you.</span>
            </h2>
            <p className="body-base">
              Have a question about a program? Ready to book your call? Or just
              not sure where to start? Reach out — we respond within 48 hours.
            </p>
          </div>

          <div className="three-col" data-testid="contact-info-grid">
            <div className="info-card">
              <div className="info-icon"><Mail size={22} color="#054452" /></div>
              <div className="info-label">EMAIL</div>
              <a
                href="mailto:info@nijahalmond.com"
                className="info-value"
                data-testid="contact-email-link"
              >
                info@nijahalmond.com
              </a>
            </div>
            <div className="info-card">
              <div className="info-icon"><Phone size={22} color="#054452" /></div>
              <div className="info-label">PHONE</div>
              <a
                href="tel:+12409419948"
                className="info-value"
                data-testid="contact-phone-link"
              >
                (240) 941-9948
              </a>
            </div>
            <div className="info-card">
              <div className="info-icon"><Globe size={22} color="#054452" /></div>
              <div className="info-label">WEBSITE</div>
              <a
                href="https://www.nijahalmond.com"
                target="_blank"
                rel="noopener noreferrer"
                className="info-value"
                data-testid="contact-website-link"
              >
                www.nijahalmond.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEND A MESSAGE */}
      <section
        id="contact-form"
        className="section-pad bg-blush scroll-to-top-anchor"
        data-testid="contact-form-section"
      >
        <div className="container-x" style={{ maxWidth: 760 }}>
          <SectionLabel center>SEND A MESSAGE</SectionLabel>
          <h2 className="section-title text-teal" style={{ textAlign: "center" }}>
            Send a <span className="italic-c text-coral">Message.</span>
          </h2>
          <p className="body-base" style={{ textAlign: "center", maxWidth: 540, margin: "0 auto 32px" }}>
            Fill out the form and Nijah’s team will be in touch within 48 hours.
          </p>

          {done ? (
            <div data-testid="contact-success" style={{
              background: "var(--white)",
              border: "1px solid rgba(13,31,36,.08)",
              borderLeft: "4px solid var(--gold)",
              padding: "32px 28px", borderRadius: 2, textAlign: "center",
            }}>
              <Crown size={32} opacity={0.85} />
              <p className="italic-c" style={{
                fontSize: 24, color: "var(--teal)", marginTop: 12, lineHeight: 1.4,
              }}>
                Your message is in. Nijah’s team will reply within 48 business hours.
              </p>
              <p className="body-base" style={{ marginTop: 8 }}>
                In the meantime, if you haven’t yet — grab the free{" "}
                <a href="/freebie" style={{ color: "var(--coral)" }}>Mirror Method</a> guide.
              </p>
            </div>
          ) : (
            <form
              className="form-row full"
              onSubmit={submit}
              style={{ maxWidth: 640, margin: "0 auto", background: "var(--white)", padding: "32px", borderRadius: 2, border: "1px solid rgba(13,31,36,.08)" }}
              data-testid="contact-form"
            >
              <div className="form-grid-2">
                <div>
                  <label className="form-label">First name</label>
                  <input className="input-base" required value={form.first_name} onChange={update("first_name")} data-testid="contact-first" />
                </div>
                <div>
                  <label className="form-label">Last name</label>
                  <input className="input-base" required value={form.last_name} onChange={update("last_name")} data-testid="contact-last" />
                </div>
              </div>
              <div>
                <label className="form-label">Email address</label>
                <input className="input-base" type="email" required value={form.email} onChange={update("email")} data-testid="contact-email" />
              </div>
              <div>
                <label className="form-label">I’m interested in…</label>
                <select className="input-base" value={form.interest} onChange={update("interest")} data-testid="contact-interest">
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Tell me where you are right now</label>
                <textarea
                  className="input-base"
                  value={form.message}
                  onChange={update("message")}
                  required
                  data-testid="contact-message"
                />
              </div>
              <button className="btn btn-teal" type="submit" disabled={loading} data-testid="contact-submit">
                {loading ? "Sending…" : "Send My Message →"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-pad bg-cream scroll-to-top-anchor" data-testid="faq-section">
        <div className="container-x" style={{ maxWidth: 820 }}>
          <SectionLabel center>FAQ</SectionLabel>
          <h2 className="section-title text-teal" style={{ textAlign: "center" }}>
            Frequently asked <span className="italic-c text-coral">questions.</span>
          </h2>
          <div className="faq-list" style={{ marginTop: 30 }}>
            {FAQ.map((f, i) => (
              <div key={i} className={`faq ${openFaq === i ? "open" : ""}`} data-testid={`faq-${i}`}>
                <button
                  className="faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-q-${i}`}
                >
                  <span>{f.q}</span>
                  <span className="plus">+</span>
                </button>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
